/**
 * Gate 1 — Submit set for approval (shown after the Review Queue is clear).
 */
import { useMemo, useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { TRUST_ATTRIBUTES, trustAttributeLabel } from "../../config/trustAttributes";

const FONT = "Lato, sans-serif";



const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const cardSx = {
  bgcolor: "#FFFFFF",
  border: "1px solid #E6E6E6",
  borderRadius: "8px",
  p: { xs: 2, md: 2.5 },
  minWidth: 0,
};

const noteFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    bgcolor: "#F5F5F5",
    fontFamily: FONT,
    fontSize: 14,
    color: "#777777",
    "& fieldset": { borderColor: "#E6E6E6" },
    "&:hover fieldset": { borderColor: "#D0D5DD" },
    "&.Mui-focused fieldset": { borderColor: "#005BF0", borderWidth: 1 },
  },
  "& textarea": { fontFamily: FONT },
};

const bannerBtnSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 13,
  textTransform: "none",
  height: 32,
  px: 1.5,
  borderRadius: "4px",
  color: "#067647",
  border: "1px solid #12B76A",
  bgcolor: "#FFFFFF",
  flex: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": { bgcolor: "#F6FEF9", border: "1px solid #12B76A" },
};

const outlineBtnSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 14,
  textTransform: "none",
  height: 40,
  px: 2,
  borderRadius: "4px",
  color: "#005BF0",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  flex: { xs: 1, sm: "none" },
  "&:hover": { bgcolor: "#F0F6FF", border: "1px solid #005BF0" },
};

const primaryBtnSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 14,
  textTransform: "none",
  height: 40,
  px: 2.25,
  borderRadius: "4px",
  color: "#FFFFFF",
  background: PRIMARY_BG,
  bgcolor: "#005BF0",
  boxShadow: "none",
  whiteSpace: "nowrap",
  flex: { xs: 1, sm: "none" },
  "&:hover": { background: PRIMARY_BG_HOVER, boxShadow: "none" },
  "&.Mui-disabled": {
    background: "#D0D5DD",
    bgcolor: "#D0D5DD",
    color: "#FFFFFF",
  },
};

function CheckMark({ passed }) {
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        bgcolor: passed ? "#2E7D32" : "#E6E6E6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: "1px",
      }}
    >
      <CheckIcon sx={{ color: "#FFFFFF", fontSize: 14 }} />
    </Box>
  );
}

function ChecklistItem({ title, detail, passed }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 1.1 }}>
      <CheckMark passed={passed} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT,
            fontWeight: 600,
            fontStyle: "normal",
            fontSize: 15,
            lineHeight: 1,
            letterSpacing: 0,
            color: "#333333",
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#777777", fontFamily: FONT, mt: 0.25, lineHeight: 1.45 }}>
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}

function buildChecklist(testCases, metrics) {
  const generated = testCases.filter((tc) => (tc.source || "Generated") === "Generated");
  const generatedReviewed = generated.every(
    (tc) => tc.status === "verified" || tc.status === "deactivated"
  );
  const generatedCount = generated.length;
  const flaggedCount = testCases.filter((tc) => tc.status === "flagged").length;
  const activeMetrics = metrics.filter((m) => m && m.id && m.status !== "deactivated");
  const coveredMetricIds = new Set(testCases.map((tc) => tc.metricId).filter(Boolean));
  const uncovered = activeMetrics.filter((m) => !coveredMetricIds.has(m.id)).length;
  const metricTotal = activeMetrics.length;
  const coveredCount = Math.max(0, metricTotal - uncovered);
  const metricsCovered = metricTotal === 0 ? true : uncovered === 0;
  const adversarialCount = testCases.filter(
    (tc) => tc.testType === "adversarial" || tc.testType === "failure_mode"
  ).length;

  return [
    {
      title: "Every generated case is reviewed",
      detail:
        generatedCount === 0
          ? "No generated cases in this set."
          : generatedReviewed
          ? `All ${generatedCount} generated cases are validated or deactivated.`
          : `${generated.filter((tc) => tc.status === "verified" || tc.status === "deactivated").length} of ${generatedCount} generated cases are validated or deactivated.`,
      passed: generatedCount === 0 || generatedReviewed,
    },
    {
      title: "Flagged cases confirmed by an SME",
      detail:
        flaggedCount === 0
          ? "No outstanding flags."
          : `${flaggedCount} flagged ${flaggedCount === 1 ? "case needs" : "cases need"} SME confirmation.`,
      passed: flaggedCount === 0,
    },
    {
      title: "Every metric in the strategy has coverage",
      detail:
        metricTotal === 0
          ? "No metrics in the strategy."
          : metricsCovered
          ? `All ${metricTotal} metrics have at least one test case.`
          : `${coveredCount} of ${metricTotal} metrics have at least one test case.`,
      passed: metricsCovered,
    },
    {
      title: "Adversarial coverage present",
      detail:
        adversarialCount > 0
          ? `${adversarialCount} adversarial and red-team ${adversarialCount === 1 ? "case" : "cases"} in the set.`
          : "No adversarial or red-team cases in the set.",
      passed: adversarialCount > 0,
    },
  ];
}

function buildComposition(testCases) {
  const counts = Object.fromEntries(TRUST_ATTRIBUTES.map((attr) => [attr, 0]));
  testCases.forEach((tc) => {
    const key = tc.trustAttribute;
    if (key && counts[key] !== undefined) counts[key] += 1;
  });
  return TRUST_ATTRIBUTES.map((attr) => ({ label: trustAttributeLabel(attr), value: counts[attr] }));
}

export default function SubmitSetForApprovalView({
  testCases = [],
  metrics = [],
  onBack,
  onCancel,
  onSend,
  onClose,
  onGoToTestSuite,
  busy = false,
}) {
  const [note, setNote] = useState("");
  const [signedOff, setSignedOff] = useState(false);
  const checklist = useMemo(() => buildChecklist(testCases, metrics), [testCases, metrics]);
  const composition = useMemo(() => buildComposition(testCases), [testCases]);
  const total = testCases.length;
  const canSend = note.trim().length > 0 && !busy && !signedOff;

  const handleSend = () => {
    if (!canSend) return;
    onSend?.(note.trim());
    setSignedOff(true);
  };

  const handleHeaderBack = () => {
    if (signedOff) onClose?.();
    else onBack?.();
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, bgcolor: "#FFFFFF", overflow: "hidden" }}>
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, md: 3 },
          py: { xs: 1.25, md: 1.75 },
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid #E6E6E6",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <IconButton onClick={handleHeaderBack} aria-label="Back" sx={{ color: "#383838", mt: 0.15, ml: -1 }}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 800, color: "#111827", fontFamily: FONT, lineHeight: 1.3 }}>
              Submit set for approval
            </Typography>
            <Typography sx={{ fontSize: { xs: 12, md: 13 }, color: "#777777", mt: 0.35, fontFamily: FONT, lineHeight: 1.45 }}>
              Head of Quality signs off the full set. Approval locks the ground truth and unlocks test suite assembly.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, md: 2.5 } }}>
        {signedOff && (
          <Box
            sx={{
              mb: { xs: 1.5, md: 2 },
              px: { xs: 1.5, md: 2 },
              py: 1.25,
              bgcolor: "#ECFDF3",
              border: "1px solid #A6F4C5",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0, flex: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2E7D32", fontSize: 22, flexShrink: 0, mt: "1px" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#2E7D32", fontFamily: FONT, lineHeight: 1.3 }}>
                  Set approved
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#2E7D32", fontFamily: FONT, lineHeight: 1.4, mt: 0.25 }}>
                  Approved by Head of Quality. Test suite is now available.
                </Typography>
              </Box>
            </Box>
            <Button disableElevation onClick={onGoToTestSuite} sx={bannerBtnSx}>
              Go to test suite
            </Button>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.2fr) minmax(0, 0.8fr)" },
            gap: { xs: 1.5, md: 2 },
            alignItems: "stretch",
          }}
        >
          <Box sx={{ ...cardSx, height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#111827", fontFamily: FONT, mb: 0.75 }}>
              Readiness Checklist
            </Typography>
            <Box sx={{ flex: 1 }}>
              {checklist.map((item, i) => (
                <Box
                  key={item.title}
                  sx={{
                    borderBottom: i < checklist.length - 1 ? "1px solid #F2F4F7" : "none",
                  }}
                >
                  <ChecklistItem title={item.title} detail={item.detail} passed={item.passed} />
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ ...cardSx, height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#111827", fontFamily: FONT, mb: 0.5 }}>
              Set Composition
            </Typography>
            <Box sx={{ flex: 1 }}>
              {composition.map((row, i) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    py: 1,
                    minWidth: 0,
                    borderBottom: i < composition.length - 1 ? "1px solid #F2F4F7" : "none",
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: "#555555", fontFamily: FONT, minWidth: 0, pr: 1, wordBreak: "break-word" }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: FONT, flexShrink: 0 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: { xs: 1.75, md: 2.25 }, width: "100%", minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#777777", fontFamily: FONT, mb: 1 }}>
            <Box component="span" sx={{ color: "#DE350B", mr: 0.25 }}>
              *
            </Box>
            Note to Approver
          </Typography>
          {signedOff ? (
            <Box
              sx={{
                width: "100%",
                bgcolor: "#F5F5F5",
                border: "1px solid #E6E6E6",
                borderRadius: "6px",
                px: { xs: 1.5, md: 2 },
                py: { xs: 1.25, md: 1.5 },
              }}
            >
              <Typography sx={{ fontSize: 14, color: "#777777", fontFamily: FONT, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {note}
              </Typography>
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={5}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Flagged cases reviewed by Dr. Mehra (Credit Risk SME). Adversarial coverage limited to prompt injection — recommend expanding red-team scenarios next cycle."
              sx={noteFieldSx}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2 },
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 1.75 },
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E6E6E6",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: signedOff ? "#00754E" : "#111827",
              fontFamily: FONT,
            }}
          >
            {signedOff ? "Signed off by Head of Quality" : `All ${total} ${total === 1 ? "case" : "cases"} dispositioned. Ready to send.`}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#777777", fontFamily: FONT, mt: 0.25 }}>
            Approval locks the ground truth and unlocks test suite assembly.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.25, justifyContent: { xs: "stretch", sm: "flex-end" }, flexShrink: 0, flexWrap: "wrap" }}>
          {signedOff ? (
            <>
              <Button disableElevation onClick={onClose} sx={outlineBtnSx}>
                Back to test cases
              </Button>
              <Button disableElevation onClick={onGoToTestSuite} sx={primaryBtnSx}>
                Go to test suite
              </Button>
            </>
          ) : (
            <>
              <Button disableElevation onClick={onCancel} disabled={busy} sx={outlineBtnSx}>
                Cancel
              </Button>
              <Button disableElevation disabled={!canSend} onClick={handleSend} sx={primaryBtnSx}>
                Send for sign off
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
